<template>
  <Teleport to="body">
    <div v-if="open" class="int-modal-overlay" @click.self="$emit('close')">
      <div class="int-modal int-modal--lg" role="dialog" aria-modal="true" @click.stop>
        <div class="int-modal__accent" />
        <header class="int-modal__header">
          <div class="int-modal__icon-wrap">
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2">
              <path stroke-linecap="round" stroke-linejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
          <div class="int-modal__titles">
            <h2>{{ isEdit ? t('integracao.forms.editTitle') : t('integracao.forms.createTitle') }}</h2>
            <p class="int-modal__subtitle">{{ t('integracao.forms.editorSubtitle') }}</p>
          </div>
          <button type="button" class="int-modal__close" :aria-label="t('common.close')" @click="$emit('close')">
            <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2">
              <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </header>

        <div class="int-modal__body">
          <section class="int-modal__section">
            <h3 class="int-modal__section-title">{{ t('integracao.forms.sectionBasic') }}</h3>
            <div class="int-modal__field">
              <label for="form-nome">{{ t('integracao.forms.formName') }}</label>
              <input id="form-nome" v-model="draft.nome" type="text" :placeholder="t('integracao.forms.formNamePlaceholder')" />
            </div>
            <div class="int-modal__field">
              <label for="form-success">{{ t('integracao.forms.successMessage') }}</label>
              <textarea
                id="form-success"
                v-model="draft.successMessage"
                rows="3"
                :placeholder="t('publicForm.defaultSuccess')"
              />
            </div>
            <div v-if="isEdit" class="int-modal__status-row">
              <span class="int-modal__status-label">{{ t('integracao.forms.active') }}</span>
              <label class="int-toggle">
                <input v-model="draft.ativo" type="checkbox" />
                <span class="int-toggle__track"><span class="int-toggle__thumb" /></span>
              </label>
            </div>
          </section>

          <section class="int-modal__section">
            <h3 class="int-modal__section-title">{{ t('integracao.forms.fieldsTitle') }}</h3>
            <p class="int-modal__hint">{{ t('integracao.forms.fieldsHint') }}</p>
            <div class="int-field-list">
              <div
                v-for="key in fieldKeys"
                :key="key"
                class="int-field-card"
                :class="{
                  'int-field-card--locked': key === 'nome',
                  'int-field-card--disabled': !draft.fieldConfig[key].enabled && key !== 'nome'
                }"
              >
                <div class="int-field-card__info">
                  <span class="int-field-card__icon">{{ fieldAbbr(key) }}</span>
                  <div>
                    <div class="int-field-card__name">{{ t(`publicForm.fields.${key}`) }}</div>
                    <div v-if="key === 'nome'" class="int-field-card__badge">{{ t('integracao.forms.alwaysRequired') }}</div>
                  </div>
                </div>
                <div class="int-field-card__toggles">
                  <label
                    class="int-toggle"
                    :class="{ 'int-toggle--disabled': key === 'nome' || !canDisable(key) }"
                  >
                    <input
                      type="checkbox"
                      :checked="draft.fieldConfig[key].enabled"
                      :disabled="key === 'nome' || !canDisable(key)"
                      @change="toggleEnabled(key, $event.target.checked)"
                    />
                    <span class="int-toggle__track"><span class="int-toggle__thumb" /></span>
                    <span class="int-toggle__label">{{ t('integracao.forms.enabled') }}</span>
                  </label>
                  <label
                    v-if="canOptional(key)"
                    class="int-toggle"
                    :class="{ 'int-toggle--disabled': !draft.fieldConfig[key].enabled }"
                  >
                    <input
                      v-model="draft.fieldConfig[key].optional"
                      type="checkbox"
                      :disabled="!draft.fieldConfig[key].enabled"
                    />
                    <span class="int-toggle__track"><span class="int-toggle__thumb" /></span>
                    <span class="int-toggle__label">{{ t('integracao.forms.optional') }}</span>
                  </label>
                </div>
              </div>
            </div>
          </section>

          <div v-if="configError" class="int-modal__alert int-modal__alert--error" role="alert">
            {{ configError }}
          </div>
        </div>

        <footer class="int-modal__footer">
          <button type="button" class="int-btn int-btn--ghost" @click="$emit('close')">
            {{ t('common.cancel') }}
          </button>
          <button type="button" class="int-btn int-btn--primary" :disabled="saving" @click="save">
            {{ saving ? t('integracao.forms.saving') : t('common.save') }}
          </button>
        </footer>
      </div>
    </div>
  </Teleport>
</template>

<script setup>
import { ref, watch, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import {
  FORM_FIELD_KEYS,
  defaultFieldConfig,
  normalizeFieldConfig,
  canDisableField,
  canToggleOptional
} from '@/utils/leadFormFieldConfig'
import '@/assets/styles/integration-modal.css'

const props = defineProps({
  open: Boolean,
  initial: { type: Object, default: null },
  saving: Boolean
})

const emit = defineEmits(['close', 'save'])
const { t } = useI18n()

const fieldKeys = FORM_FIELD_KEYS
const isEdit = computed(() => !!props.initial?.id)
const configError = ref('')

const draft = ref({
  nome: '',
  successMessage: '',
  ativo: true,
  fieldConfig: defaultFieldConfig()
})

const FIELD_ABBR = {
  nome: 'Nm',
  empresa: 'Em',
  cargo: 'Cg',
  whatsapp: 'Wa',
  email: '@',
  cnpj: 'Id'
}

function fieldAbbr (key) {
  return FIELD_ABBR[key] || key.slice(0, 2).toUpperCase()
}

watch(() => props.open, (v) => {
  if (!v) return
  configError.value = ''
  if (props.initial) {
    draft.value = {
      nome: props.initial.nome || '',
      successMessage: props.initial.successMessage || '',
      ativo: props.initial.ativo !== false,
      fieldConfig: { ...defaultFieldConfig(), ...props.initial.fieldConfig }
    }
  } else {
    draft.value = {
      nome: '',
      successMessage: t('publicForm.defaultSuccess'),
      ativo: true,
      fieldConfig: defaultFieldConfig()
    }
  }
})

function canDisable (key) {
  try {
    return canDisableField(draft.value.fieldConfig, key)
  } catch {
    return false
  }
}

function canOptional (key) {
  return canToggleOptional(key)
}

function toggleEnabled (key, enabled) {
  if (key === 'nome') return
  draft.value.fieldConfig[key].enabled = enabled
  if (!enabled) draft.value.fieldConfig[key].optional = false
}

function save () {
  configError.value = ''
  if (!draft.value.nome?.trim()) {
    configError.value = t('integracao.forms.nameRequired')
    return
  }
  try {
    const fieldConfig = normalizeFieldConfig(draft.value.fieldConfig)
    emit('save', {
      nome: draft.value.nome.trim(),
      successMessage: draft.value.successMessage?.trim(),
      ativo: draft.value.ativo,
      fieldConfig
    })
  } catch (e) {
    if (e.message === 'CONTACT_REQUIRED') {
      configError.value = t('integracao.forms.contactRequired')
    } else {
      configError.value = e.message
    }
  }
}
</script>
